import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PricingSection = () => {
  const navigate = useNavigate();

  const plans = [
    {
      id: 1,
      name: 'Free',
      price: '₹0',
      period: '/month',
      description: 'Perfect for getting started with basic planning',
      features: [
        'Basic Business Model Canvas',
        'Manual data entry only',
        'Up to 3 goal tracking',
        'Basic health metrics',
        'Community support',
        'Export to PDF (watermarked)'
      ],
      limitations: [
        'No AI suggestions',
        'No predictive analysis',
        'Limited reports'
      ],
      cta: 'Get Started',
      popular: false,
      variant: 'outline'
    },
    {
      id: 2,
      name: 'Growth',
      price: '₹2,499',
      period: '/month',
      description: 'For growing businesses ready to scale',
      features: [
        'Full Business Model Canvas with AI',
        'Excel upload & manual entry',
        'Unlimited goal tracking',
        'Advanced health metrics',
        'Predictive analysis (1 year)',
        'Priority email support',
        'Unlimited PDF exports',
        'Basic API access'
      ],
      limitations: [],
      cta: 'Start Free Trial',
      popular: true,
      variant: 'default'
    },
    {
      id: 3,
      name: 'Scale',
      price: '₹6,799',
      period: '/month',
      description: 'Advanced features for established SMEs',
      features: [
        'Everything in Growth',
        'Predictive analysis (2 years)',
        'Advanced risk assessment',
        'Custom report generation',
        'Team collaboration (5 users)',
        'Priority phone support',
        'White-label reports',
        'Full API access',
        'Quarterly strategy reviews'
      ],
      limitations: [],
      cta: 'Start Free Trial',
      popular: false,
      variant: 'outline'
    },
    {
      id: 4,
      name: 'Enterprise',
      price: 'Custom',
      period: '',
      description: 'Tailored solutions for large organizations',
      features: [
        'Everything in Scale',
        'Unlimited team members',
        'Custom AI model training',
        'Dedicated account manager',
        'On-premise deployment option',
        '24/7 premium support',
        'Custom integrations',
        'Advanced security features',
        'SLA guarantees'
      ],
      limitations: [],
      cta: 'Contact Sales',
      popular: false,
      variant: 'secondary'
    }
  ];

  const handlePlanClick = (plan) => {
    if (plan?.name === 'Enterprise') {
      window.scrollTo({ top: document.body?.scrollHeight, behavior: 'smooth' });
    } else {
      navigate('/signup-and-onboarding');
    }
  };

  return (
    <section className="py-12 md:py-16 lg:py-20 px-4 md:px-6 lg:px-8 bg-muted/30">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8 md:mb-12 lg:mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full mb-4">
            <Icon name="DollarSign" size={16} color="var(--color-primary)" />
            <span className="text-xs md:text-sm font-medium text-primary font-caption">
              Flexible Pricing
            </span>
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
            Choose Your Growth Plan
          </h2>
          <p className="text-base md:text-lg text-muted-foreground font-body max-w-3xl mx-auto">
            Start free and upgrade as you grow. All paid plans include a 14-day free trial with no credit card required.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {plans?.map((plan) => (
            <div
              key={plan?.id}
              className={`relative bg-card border rounded-xl p-6 md:p-8 transition-smooth hover:elevation-lg ${
                plan?.popular
                  ? 'border-primary elevation-md lg:scale-105' :'border-border'
              }`}
            >
              {plan?.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-primary text-primary-foreground text-xs font-medium font-caption rounded-full">
                  Most Popular
                </div>
              )}

              <div className="mb-6">
                <h3 className="text-xl md:text-2xl font-heading font-bold text-foreground mb-2">
                  {plan?.name}
                </h3>
                <div className="flex items-baseline gap-1 mb-3">
                  <span className="text-3xl md:text-4xl font-heading font-bold text-foreground">
                    {plan?.price}
                  </span>
                  {plan?.period && (
                    <span className="text-sm text-muted-foreground font-body">
                      {plan?.period}
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground font-body">
                  {plan?.description}
                </p>
              </div>

              <Button
                variant={plan?.variant}
                size="default"
                onClick={() => handlePlanClick(plan)}
                fullWidth
                className="mb-6"
              >
                {plan?.cta}
              </Button>

              <div className="space-y-3">
                {plan?.features?.map((feature, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <div className="w-5 h-5 rounded-full bg-success/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Icon name="Check" size={12} color="var(--color-success)" strokeWidth={3} />
                    </div>
                    <span className="text-xs md:text-sm text-foreground font-body">
                      {feature}
                    </span>
                  </div>
                ))}
                {plan?.limitations?.map((limitation, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <div className="w-5 h-5 rounded-full bg-muted flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Icon name="X" size={12} color="var(--color-muted-foreground)" strokeWidth={3} />
                    </div>
                    <span className="text-xs md:text-sm text-muted-foreground font-body">
                      {limitation}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 md:mt-12 text-center">
          <p className="text-sm text-muted-foreground font-body mb-4">
            All plans include SSL security, data encryption, and GDPR compliance
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6">
            <div className="flex items-center gap-2">
              <Icon name="Shield" size={16} color="var(--color-success)" />
              <span className="text-xs md:text-sm text-muted-foreground font-caption">
                Secure Payment
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="Lock" size={16} color="var(--color-success)" />
              <span className="text-xs md:text-sm text-muted-foreground font-caption">
                Data Encrypted
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="RefreshCw" size={16} color="var(--color-success)" />
              <span className="text-xs md:text-sm text-muted-foreground font-caption">
                Cancel Anytime
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;